import React from 'react';
import { useTranslation } from 'react-i18next';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScheduleDatetimePicker } from '@/components/publishing/ScheduleDatetimePicker';

export type PublishWhen = 'now' | 'later';

interface Props {
  idPrefix: string;
  when: PublishWhen;
  onWhenChange: (when: PublishWhen) => void;
  datetimeLocal: string;
  onDatetimeLocalChange: (value: string) => void;
  minDatetimeLocal: string;
  error?: string | null;
}

export const PublishWhenControl: React.FC<Props> = ({
  idPrefix,
  when,
  onWhenChange,
  datetimeLocal,
  onDatetimeLocalChange,
  minDatetimeLocal,
  error,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium">{t('publishing.when.label')}</p>
      <RadioGroup
        aria-label={t('publishing.when.label')}
        className="flex gap-4"
        value={when}
        onValueChange={(value) => {
          if (value === 'now' || value === 'later') onWhenChange(value);
        }}
      >
        {(['now', 'later'] as const).map((value) => (
          <div key={value} className="flex items-center gap-2">
            <RadioGroupItem
              id={`${idPrefix}-when-${value}`}
              value={value}
              data-testid={`${idPrefix}-when-${value}`}
            />
            <label className="text-sm" htmlFor={`${idPrefix}-when-${value}`}>
              {t(`publishing.when.${value}`)}
            </label>
          </div>
        ))}
      </RadioGroup>
      {when === 'later' ? (
        <div className="space-y-1.5">
          <label className="text-xs font-medium" htmlFor={`${idPrefix}-when-datetime`}>
            {t('publishing.schedule.datetimeLabel')}
          </label>
          <ScheduleDatetimePicker
            id={`${idPrefix}-when-datetime`}
            testId={`${idPrefix}-when-datetime`}
            min={minDatetimeLocal}
            value={datetimeLocal}
            onChange={onDatetimeLocalChange}
          />
        </div>
      ) : null}
      {error ? (
        <p className="text-sm text-destructive" data-testid={`${idPrefix}-when-error`}>
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default PublishWhenControl;
